import axios from "axios";
import { translateWorkspaceText } from "@/locales/workspaceExtras";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
  xsrfCookieName: "td_csrf",
  xsrfHeaderName: "X-CSRF-Token",
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string" && typeof window !== "undefined") {
      const lang = window.localStorage?.getItem("td_lang") || "en";
      error.response.data.detail = translateWorkspaceText(lang, detail);
    }
    return Promise.reject(error);
  },
);

/**
 * Stream AI draft via SSE (Server-Sent Events).
 * Calls `onToken(text)` for each token, `onDone({id,length})` when finished,
 * `onError(err)` on stream failure. Returns a controller with `abort()`.
 */
export function streamAiDraft(payload, { onStart, onToken, onDone, onError }) {
  const controller = new AbortController();
  const csrfToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("td_csrf="))
    ?.split("=")[1];

  (async () => {
    try {
      const res = await fetch(`${API}/ai/draft/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
          "X-CSRF-Token": csrfToken ? decodeURIComponent(csrfToken) : "",
        },
        credentials: "include",
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => "");
        let detail;
        try { detail = JSON.parse(text)?.detail; } catch { detail = null; }
        const err = new Error(detail?.message || (typeof detail === "string" ? detail : text) || `HTTP ${res.status}`);
        err.status = res.status;
        err.code = detail?.code;
        err.detail = detail;
        throw err;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE — events separated by blank line
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const raw of events) {
          if (!raw.trim()) continue;
          const lines = raw.split("\n");
          let eventName = "message";
          let dataStr = "";
          for (const line of lines) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (!dataStr) continue;
          let data;
          try { data = JSON.parse(dataStr); } catch { continue; }

          if (eventName === "start") { onStart && onStart(data); }
          else if (eventName === "done") { onDone && onDone(data); }
          else if (eventName === "error") { onError && onError(new Error(data.error || "stream error")); return; }
          else if (data.token !== undefined) { onToken && onToken(data.token); }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      onError && onError(err);
    }
  })();

  return controller;
}

export const api = {
  listRooms: () => client.get("/deal-rooms").then((r) => r.data),
  createRoom: (data) => client.post("/deal-rooms", data).then((r) => r.data),
  getRoom: (id) => client.get(`/deal-rooms/${id}`).then((r) => r.data),
  listRoomInvitations: () => client.get("/room-invitations").then((r) => r.data),
  resolveRoomInvitation: (token) => client.post("/room-invitations/resolve", { token }).then((r) => r.data),
  acceptRoomInvitation: (id) => client.post(`/room-invitations/${id}/accept`).then((r) => r.data),
  inviteToRoom: (id, data) => client.post(`/deal-rooms/${id}/invite`, data).then((r) => r.data),
  revokeRoomInvitation: (id, data) => client.post(`/deal-rooms/${id}/revoke-invitation`, data).then((r) => r.data),
  roomMessage: (id, data) => client.post(`/deal-rooms/${id}/messages`, data).then((r) => r.data),
  proposeRoomTerms: (id, data) => client.post(`/deal-rooms/${id}/terms`, data).then((r) => r.data),
  acceptRoomTerms: (id, data) => client.post(`/deal-rooms/${id}/terms/accept`, data).then((r) => r.data),
  publishRoomDocument: (id, data) => client.post(`/deal-rooms/${id}/documents`, data).then((r) => r.data),
  uploadRoomFile: (id, data) => client.post(`/deal-rooms/${id}/upload`, data).then((r) => r.data),
  getRoomDocument: (id, documentId) => client.get(`/deal-rooms/${id}/documents/${documentId}`).then((r) => r.data),
  downloadRoomDocument: (id, documentId, format) => client.get(`/deal-rooms/${id}/documents/${documentId}/download`, { params: { format }, responseType: "blob" }).then((r) => r.data),
  closeRoom: (id, data) => client.post(`/deal-rooms/${id}/close`, data).then((r) => r.data),
  register: (data) => client.post("/auth/register", data).then((r) => r.data),
  login: (data) => client.post("/auth/login", data).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  updateProfile: (data) => client.patch("/auth/me", data).then((r) => r.data),
  changePassword: (data) => client.post("/auth/change-password", data).then((r) => r.data),
  requestEmailVerification: (data) => client.post("/auth/verification/request", data).then((r) => r.data),
  confirmEmailVerification: (data) => client.post("/auth/verification/confirm", data).then((r) => r.data),
  forgotPassword: (data) => client.post("/auth/password/forgot", data).then((r) => r.data),
  resetPassword: (data) => client.post("/auth/password/reset", data).then((r) => r.data),
  getPreferences: () => client.get("/auth/preferences").then((r) => r.data),
  updatePreferences: (data) => client.patch("/auth/preferences", data).then((r) => r.data),
  exportAccountData: () => client.get("/auth/export").then((r) => r.data),
  getOrganization: () => client.get("/organization").then((r) => r.data),
  updateOrganization: (data) => client.patch("/organization", data).then((r) => r.data),
  listOrganizationMembers: () => client.get("/organization/members").then((r) => r.data),
  listOrganizationInvitations: () => client.get("/organization/invitations").then((r) => r.data),
  listMyOrganizationInvitations: () => client.get("/organization/invitations/mine").then((r) => r.data),
  inviteOrganizationMember: (data) => client.post("/organization/invitations", data).then((r) => r.data),
  acceptOrganizationInvitation: (id) => client.post(`/organization/invitations/${id}/accept`).then((r) => r.data),
  revokeOrganizationInvitation: (id) => client.delete(`/organization/invitations/${id}`).then((r) => r.data),
  updateOrganizationMember: (id, data) => client.patch(`/organization/members/${id}`, data).then((r) => r.data),
  removeOrganizationMember: (id) => client.delete(`/organization/members/${id}`).then((r) => r.data),
  listExpertRequests: () => client.get("/expert-requests").then((r) => r.data),
  getExpertRequest: (id) => client.get(`/expert-requests/${id}`).then((r) => r.data),
  createExpertRequest: (data) => client.post("/expert-requests", data).then((r) => r.data),
  expertClientAction: (id, data) => client.post(`/expert-requests/${id}/client-action`, data).then((r) => r.data),
  createExpertMessage: (id, data) => client.post(`/expert-requests/${id}/messages`, data).then((r) => r.data),
  adminListExpertRequests: (status) => client.get("/admin/expert-requests", { params: status ? { status } : {} }).then((r) => r.data),
  adminExpertAction: (id, data) => client.post(`/admin/expert-requests/${id}/action`, data).then((r) => r.data),
  listIntegrationApiKeys: () => client.get("/integrations/api-keys").then((r) => r.data),
  createIntegrationApiKey: (data) => client.post("/integrations/api-keys", data).then((r) => r.data),
  revokeIntegrationApiKey: (id) => client.delete(`/integrations/api-keys/${id}`).then((r) => r.data),
  listIntegrationWebhooks: () => client.get("/integrations/webhooks").then((r) => r.data),
  createIntegrationWebhook: (data) => client.post("/integrations/webhooks", data).then((r) => r.data),
  updateIntegrationWebhook: (id, data) => client.patch(`/integrations/webhooks/${id}`, data).then((r) => r.data),
  deleteIntegrationWebhook: (id) => client.delete(`/integrations/webhooks/${id}`).then((r) => r.data),
  testIntegrationWebhook: (id) => client.post(`/integrations/webhooks/${id}/test`).then((r) => r.data),
  listIntegrationEvents: (limit = 100) => client.get("/integrations/events", { params: { limit } }).then((r) => r.data),
  getOdooConnection: () => client.get("/integrations/providers/odoo").then((r) => r.data),
  saveOdooConnection: (data) => client.put("/integrations/providers/odoo", data).then((r) => r.data),
  disconnectOdoo: () => client.delete("/integrations/providers/odoo").then((r) => r.data),
  testOdooConnection: () => client.post("/integrations/providers/odoo/test").then((r) => r.data),
  syncOdoo: (data) => client.post("/integrations/providers/odoo/sync", data).then((r) => r.data),
  listExternalShares: (dealId) => client.get("/shares", { params: { deal_id: dealId } }).then((r) => r.data),
  createExternalShare: (data) => client.post("/shares", data).then((r) => r.data),
  revokeExternalShare: (id) => client.delete(`/shares/${id}`).then((r) => r.data),
  listExternalShareActivity: (id) => client.get(`/shares/${id}/activity`).then((r) => r.data),
  getPublicShare: (token) => client.get("/public/share", { headers: { "X-Share-Token": token } }).then((r) => r.data),
  signPublicShare: (token, data) => client.post("/public/share/sign", data, { headers: { "X-Share-Token": token } }).then((r) => r.data),
  downloadPublicShare: async (token, format = "pdf") => {
    const response = await client.get("/public/share/download", { params: { format }, headers: { "X-Share-Token": token }, responseType: "blob" });
    const disposition = response.headers["content-disposition"] || "";
    const match = /filename="([^"]+)"/.exec(disposition);
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = match ? match[1] : `document.${format}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
  listTasks: (status) => client.get("/tasks", { params: status ? { status } : {} }).then((r) => r.data),
  createTask: (data) => client.post("/tasks", data).then((r) => r.data),
  updateTask: (id, data) => client.patch(`/tasks/${id}`, data).then((r) => r.data),
  deleteTask: (id) => client.delete(`/tasks/${id}`).then((r) => r.data),
  listNotifications: (unreadOnly = false) => client.get("/notifications", { params: unreadOnly ? { unread_only: true } : {} }).then((r) => r.data),
  notificationUnreadCount: () => client.get("/notifications/unread-count").then((r) => r.data),
  markNotificationRead: (id) => client.patch(`/notifications/${id}/read`).then((r) => r.data),
  markAllNotificationsRead: () => client.post("/notifications/read-all").then((r) => r.data),
  deleteNotification: (id) => client.delete(`/notifications/${id}`).then((r) => r.data),
  listFiles: (dealId) => client.get("/files", { params: dealId ? { deal_id: dealId } : {} }).then((r) => r.data),
  uploadFile: (formData) => client.post("/files", formData).then((r) => r.data),
  updateFile: (id, data) => client.patch(`/files/${id}`, data).then((r) => r.data),
  deleteFile: (id) => client.delete(`/files/${id}`).then((r) => r.data),
  saveDraftExport: (id, format = "pdf") => client.post(`/files/from-draft/${id}`, null, { params: { format } }).then((r) => r.data),
  downloadFile: async (item) => {
    const response = await client.get(`/files/${item.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = item.display_name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
  listReviews: (dealId) => client.get("/reviews", { params: dealId ? { deal_id: dealId } : {} }).then((r) => r.data),
  createReview: (data) => client.post("/reviews", data).then((r) => r.data),
  updateReviewFinding: (reviewId, findingId, data) => client.patch(`/reviews/${reviewId}/findings/${findingId}`, data).then((r) => r.data),
  extractReviewDealData: (reviewId) => client.post(`/reviews/${reviewId}/deal-data`).then((r) => r.data),
  confirmReviewDealData: (reviewId, suggestionIds) => client.post(`/reviews/${reviewId}/deal-data/confirm`, { suggestion_ids: suggestionIds }).then((r) => r.data),
  deleteReview: (id) => client.delete(`/reviews/${id}`).then((r) => r.data),
  getDealConsistency: (dealId) => client.get(`/deals/${dealId}/consistency-checks`).then((r) => r.data),
  runDealConsistency: (dealId) => client.post(`/deals/${dealId}/consistency-checks`).then((r) => r.data),
  updateDealDiscrepancy: (dealId, reportId, discrepancyId, data) => client.patch(`/deals/${dealId}/consistency-checks/${reportId}/discrepancies/${discrepancyId}`, data).then((r) => r.data),
  submitIntake: (data) => client.post("/intake", data).then((r) => r.data),
  listIntakes: () => client.get("/intake").then((r) => r.data),
  updateIntakeStatus: (id, status) => client.patch(`/intake/${id}`, { status }).then((r) => r.data),
  submitEarlyAccess: (data) => client.post("/early-access", data).then((r) => r.data),
  listEarlyAccess: () => client.get("/early-access").then((r) => r.data),
  requestDemo: (data) => client.post("/marketing/demo-requests", data).then((r) => r.data),
  marketingSummary: () => client.get("/marketing/admin/summary").then((r) => r.data),
  listDeals: () => client.get("/deals").then((r) => r.data),
  getDeal: (id) => client.get(`/deals/${id}`).then((r) => r.data),
  createDeal: (data) => client.post("/deals", data).then((r) => r.data),
  updateDeal: (id, data) => client.patch(`/deals/${id}`, data).then((r) => r.data),
  deleteDeal: (id) => client.delete(`/deals/${id}`).then((r) => r.data),
  archiveDeal: (id) => client.post(`/deals/${id}/archive`).then((r) => r.data),
  restoreDeal: (id) => client.post(`/deals/${id}/restore`).then((r) => r.data),
  listDealMessages: (dealId) => client.get(`/deals/${dealId}/messages`).then((r) => r.data),
  createDealMessage: (dealId, data) => client.post(`/deals/${dealId}/messages`, data).then((r) => r.data),
  deleteDealMessage: (dealId, messageId) => client.delete(`/deals/${dealId}/messages/${messageId}`).then((r) => r.data),
  dealsEnums: () => client.get("/deals/enums").then((r) => r.data),
  dealsNextReference: () => client.get("/deals/next-reference").then((r) => r.data),
  listTemplates: () => client.get("/templates").then((r) => r.data),
  adminSummary: () => client.get("/admin/summary").then((r) => r.data),
  getBilling: () => client.get("/billing").then((r) => r.data),
  createBillingCheckout: (data) => client.post("/billing/checkout-session", data).then((r) => r.data),
  createBillingPortal: () => client.post("/billing/portal-session").then((r) => r.data),
  adminDocuments: (params = {}) => client.get("/admin/documents", { params }).then((r) => r.data),
  adminDownloadDocument: async (item, format = "pdf") => {
    const response = await client.get(`/admin/documents/${item.kind}/${item.id}/download`, {
      params: item.kind === "draft" ? { format } : {}, responseType: "blob",
    });
    const disposition = response.headers["content-disposition"] || "";
    const match = /filename="?([^";]+)"?/.exec(disposition);
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = match ? match[1] : item.name;
    document.body.appendChild(anchor);
    anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
  },
  // Iteration C — Company & Counterparties
  getCompany: () => client.get("/company").then((r) => r.data),
  saveCompany: (data) => client.put("/company", data).then((r) => r.data),
  listCounterparties: () => client.get("/counterparties").then((r) => r.data),
  getCounterparty: (id) => client.get(`/counterparties/${id}`).then((r) => r.data),
  createCounterparty: (data) => client.post("/counterparties", data).then((r) => r.data),
  updateCounterparty: (id, data) => client.patch(`/counterparties/${id}`, data).then((r) => r.data),
  deleteCounterparty: (id) => client.delete(`/counterparties/${id}`).then((r) => r.data),
  aiDraft: (data) => client.post("/ai/draft", data).then((r) => r.data),
  capabilities: () => client.get("/capabilities").then((r) => r.data),
  listDrafts: (dealId) => client.get("/drafts", { params: dealId ? { deal_id: dealId } : {} }).then((r) => r.data),
  createDraft: (data) => client.post("/drafts", data).then((r) => r.data),
  getDraft: (id) => client.get(`/drafts/${id}`).then((r) => r.data),
  updateDraft: (id, data) => client.patch(`/drafts/${id}`, data).then((r) => r.data),
  updateDraftWorkflow: (id, data) => client.post(`/drafts/${id}/workflow`, data).then((r) => r.data),
  deleteDraft: (id) => client.delete(`/drafts/${id}`).then((r) => r.data),
  exportDraft: (id, format = "docx") => {
    return fetch(`${API}/drafts/${id}/export?format=${format}`, {
      credentials: "include",
    }).then(async (res) => {
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : `draft.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
  },
  streamAiDraft,
};

export default client;
